/**
 * JobExecutionPanel · iter303ak-fix32
 * ─────────────────────────────────────────────────────────────────────
 * Embed panel opened by the Runner's WorkExecutionLauncher via
 * `?embed=job-execution`. Hosts per-child:
 *   1. Contractor summary rows (existing — start / complete / check dates
 *      per contractor type · display-only rollup)
 *   2. Work Breakdown (NEW · line-level ticks grouped by component_type)
 *   3. Daily Log (NEW · per-day journal · date · hours · weather ·
 *      notes · optional photo attachments)
 * Plus:
 *   4. Extras Capture (existing · unchanged)
 *   5. Contractor Sign-off (existing · new gate — all lines must be
 *      ticked + sign-off entered before "Sign off & return to Runner"
 *      is enabled).
 *
 * All state persists to `parent.work_execution` via PATCH on the
 * appraisal. Photo uploads go through the shared /api/attachments
 * multipart endpoint (scope=appraisal, kind="During Photos") and only
 * the attachment_id is stored inside the daily log entry.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { CheckCircle2, Plus, Trash2, ClipboardList, Wrench, DollarSign, AlertTriangle, Save, ChevronDown, ChevronRight, Camera, Cloud, Clock, CalendarDays, Image as ImageIcon, X } from 'lucide-react';
import ForemanLogSessionCard from '@/components/ForemanLogSessionCard';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const nz = (n) => Number(n || 0).toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const attachmentSrc = (attId) => {
  const base = `${BACKEND}/api/attachments/${attId}/download`;
  return base;
};

export default function JobExecutionPanel({ appraisalId }) {
  const [appraisal, setAppraisal] = useState(null);
  const [children, setChildren] = useState([]);
  const [contractorOverviews, setContractorOverviews] = useState({});
  const [componentBreakdowns, setComponentBreakdowns] = useState({}); // {child_id: {groups: []}}
  const [workExecution, setWorkExecution] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [expandedChildren, setExpandedChildren] = useState({});   // {child_id: bool}
  const [expandedGroups, setExpandedGroups] = useState({});       // {`${child_id}|${component_type}`: bool}
  const [uploadingKey, setUploadingKey] = useState(null);

  const load = useCallback(async () => {
    if (!appraisalId) return;
    setLoading(true);
    try {
      const { data: parent } = await api.get(`/appraisals/${appraisalId}`);
      setAppraisal(parent);
      const werr = parent?.work_execution || {};
      setWorkExecution({
        per_child_lines: werr.per_child_lines || {},                   // contractor summary
        per_child_breakdown_ticks: werr.per_child_breakdown_ticks || {}, // NEW · line-level
        per_child_daily_logs: werr.per_child_daily_logs || {},         // NEW · per-day journal
        extras: Array.isArray(werr.extras) ? werr.extras : [],
        contractor_signoff_by: werr.contractor_signoff_by || '',
        signed_off: !!werr.signed_off,
        signed_off_at: werr.signed_off_at || null,
      });

      const decisions = parent?.child_decisions || {};
      const accepted = Object.entries(decisions)
        .filter(([, d]) => d?.decision === 'accepted')
        .map(([child_id, d]) => ({ child_id, ...d }));
      const childDocs = [];
      const overviews = {};
      const breakdowns = {};
      for (const c of accepted) {
        try {
          const { data: cdoc } = await api.get(`/appraisals/${c.child_id}`);
          childDocs.push({ ...c, doc: cdoc });
        } catch { childDocs.push(c); }
        try {
          const { data: ov } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
          overviews[c.child_id] = ov;
        } catch { /* silent */ }
        try {
          const { data: br } = await api.get(`/appraisals/${c.child_id}/component-lines-summary`);
          breakdowns[c.child_id] = br;
        } catch { /* silent */ }
      }
      setChildren(childDocs);
      setContractorOverviews(overviews);
      setComponentBreakdowns(breakdowns);
      // Auto-expand first child accordion for convenience.
      setExpandedChildren(prev => {
        const next = { ...prev };
        if (childDocs.length && Object.keys(next).length === 0) next[childDocs[0].child_id] = true;
        return next;
      });
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [appraisalId]);

  useEffect(() => { load(); }, [load]);

  // ══════════════════════════════════════════════════════════════════
  // 1 · Contractor summary rows (per child × contractor_type)
  // ══════════════════════════════════════════════════════════════════
  const contractorRows = useMemo(() => {
    const map = {};
    for (const c of children) {
      const ov = contractorOverviews[c.child_id];
      const assignments = ov?.type_assignments || {};
      map[c.child_id] = Object.entries(assignments).map(([ctype, cid]) => ({ ctype, contractor_id: cid }));
    }
    return map;
  }, [children, contractorOverviews]);

  const getLine = (child_id, ctype) =>
    (workExecution.per_child_lines?.[child_id]?.[ctype]) || { start_date: '', completion_date: '', checked_date: '', notes: '' };

  const setLine = (child_id, ctype, patch) => {
    setWorkExecution(prev => ({
      ...prev,
      per_child_lines: {
        ...(prev.per_child_lines || {}),
        [child_id]: {
          ...(prev.per_child_lines?.[child_id] || {}),
          [ctype]: { ...getLine(child_id, ctype), ...patch },
        },
      },
    }));
  };

  // ══════════════════════════════════════════════════════════════════
  // 2 · Work Breakdown (line-level ticks)
  // ══════════════════════════════════════════════════════════════════
  const getTick = (child_id, line_id) =>
    (workExecution.per_child_breakdown_ticks?.[child_id]?.[line_id]) || { done: false, done_at: null, notes: '' };

  const toggleTick = (child_id, line_id) => {
    const cur = getTick(child_id, line_id);
    setWorkExecution(prev => ({
      ...prev,
      per_child_breakdown_ticks: {
        ...(prev.per_child_breakdown_ticks || {}),
        [child_id]: {
          ...(prev.per_child_breakdown_ticks?.[child_id] || {}),
          [line_id]: cur.done
            ? { done: false, done_at: null, notes: cur.notes || '' }
            : { done: true, done_at: new Date().toISOString(), notes: cur.notes || '' },
        },
      },
    }));
  };

  const setTickNote = (child_id, line_id, note) => {
    const cur = getTick(child_id, line_id);
    setWorkExecution(prev => ({
      ...prev,
      per_child_breakdown_ticks: {
        ...(prev.per_child_breakdown_ticks || {}),
        [child_id]: {
          ...(prev.per_child_breakdown_ticks?.[child_id] || {}),
          [line_id]: { ...cur, notes: note },
        },
      },
    }));
  };

  const allLinesTicked = useMemo(() => {
    for (const c of children) {
      const br = componentBreakdowns[c.child_id];
      if (!br) continue;
      for (const g of br.groups || []) {
        for (const l of g.lines || []) {
          if (!getTick(c.child_id, l.line_id).done) return false;
        }
      }
    }
    return true;
  }, [children, componentBreakdowns, workExecution.per_child_breakdown_ticks]);   

  const totalLineCount = useMemo(() => {
    let total = 0;
    for (const c of children) {
      const br = componentBreakdowns[c.child_id];
      if (!br) continue;
      for (const g of br.groups || []) total += (g.lines || []).length;
    }
    return total;
  }, [children, componentBreakdowns]);

  const tickedLineCount = useMemo(() => {
    let done = 0;
    for (const c of children) {
      const br = componentBreakdowns[c.child_id];
      if (!br) continue;
      for (const g of br.groups || []) {
        for (const l of g.lines || []) if (getTick(c.child_id, l.line_id).done) done += 1;
      }
    }
    return done;
  }, [children, componentBreakdowns, workExecution.per_child_breakdown_ticks]);   

  // ══════════════════════════════════════════════════════════════════
  // 2.5 · Per-child Sign-off (iter303ak-fix35)
  // ────────────────────────────────────────────────────────────────────
  // Each child gets its own completion date + optional notes, feeding
  // the Certificate of Completion at Paid T20. Auto-suggests the MAX
  // completion_date across all contractors on the child (Qa=a).
  // ══════════════════════════════════════════════════════════════════
  const getChildSignoff = (child_id) =>
    (workExecution.per_child_signoff?.[child_id]) || { completion_date: '', notes: '', signed_off_at: null, signed_off_by: '' };

  const setChildSignoff = (child_id, patch) => {
    setWorkExecution(prev => ({
      ...prev,
      per_child_signoff: {
        ...(prev.per_child_signoff || {}),
        [child_id]: { ...((prev.per_child_signoff || {})[child_id] || {}), ...patch },
      },
    }));
  };

  const suggestedChildCompletion = (child_id) => {
    // MAX completion_date across contractor rows. Falls back to today
    // if there's a partial state (e.g. only some contractor completion
    // dates entered).
    const linesMap = (workExecution.per_child_lines || {})[child_id] || {};
    const dates = Object.values(linesMap)
      .map(l => l?.completion_date)
      .filter(d => d && d.length >= 10)
      .sort();
    return dates.length ? dates[dates.length - 1] : '';
  };

  // ══════════════════════════════════════════════════════════════════
  // 3 · Daily Log (per child · date · hours · weather · notes · photos)
  // ══════════════════════════════════════════════════════════════════
  const addDailyLog = (child_id) => {
    setWorkExecution(prev => ({
      ...prev,
      per_child_daily_logs: {
        ...(prev.per_child_daily_logs || {}),
        [child_id]: [
          ...((prev.per_child_daily_logs || {})[child_id] || []),
          {
            id: `dl_${Date.now()}`,
            date: new Date().toISOString().slice(0, 10),
            hours: '',
            weather: '',
            notes: '',
            photos: [],   // [{ attachment_id, filename }]
          },
        ],
      },
    }));
  };

  const patchDailyLog = (child_id, id, patch) => {
    setWorkExecution(prev => ({
      ...prev,
      per_child_daily_logs: {
        ...(prev.per_child_daily_logs || {}),
        [child_id]: ((prev.per_child_daily_logs || {})[child_id] || []).map(l => l.id === id ? { ...l, ...patch } : l),
      },
    }));
  };

  const removeDailyLog = (child_id, id) => {
    setWorkExecution(prev => ({
      ...prev,
      per_child_daily_logs: {
        ...(prev.per_child_daily_logs || {}),
        [child_id]: ((prev.per_child_daily_logs || {})[child_id] || []).filter(l => l.id !== id),
      },
    }));
  };

  const uploadPhoto = async (child_id, log_id, file) => {
    if (!file) return;
    setUploadingKey(`${child_id}|${log_id}`);
    setSaveErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('scope', 'appraisal');
      fd.append('scope_id', appraisalId);
      fd.append('kind', 'During Photos');
      fd.append('notes', `Daily log ${log_id}`);
      const { data } = await api.post('/attachments', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Append to daily log photos.
      setWorkExecution(prev => ({
        ...prev,
        per_child_daily_logs: {
          ...(prev.per_child_daily_logs || {}),
          [child_id]: ((prev.per_child_daily_logs || {})[child_id] || []).map(l =>
            l.id === log_id
              ? { ...l, photos: [...(l.photos || []), { attachment_id: data._id, filename: data.original_filename }] }
              : l,
          ),
        },
      }));
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Photo upload failed');
    } finally {
      setUploadingKey(null);
    }
  };

  const removePhoto = (child_id, log_id, attachment_id) => {
    // We only remove the reference — the file stays in /api/attachments so
    // it remains available in the appraisal's document library. Trevor can
    // hard-delete via the attachments UI if he wants it gone.
    setWorkExecution(prev => ({
      ...prev,
      per_child_daily_logs: {
        ...(prev.per_child_daily_logs || {}),
        [child_id]: ((prev.per_child_daily_logs || {})[child_id] || []).map(l =>
          l.id === log_id
            ? { ...l, photos: (l.photos || []).filter(p => p.attachment_id !== attachment_id) }
            : l,
        ),
      },
    }));
  };

  // ══════════════════════════════════════════════════════════════════
  // 4 · Extras Capture
  // ══════════════════════════════════════════════════════════════════
  const addExtra = () => {
    setWorkExecution(prev => ({
      ...prev,
      extras: [
        ...(prev.extras || []),
        { id: `extra_${Date.now()}`, description: '', cost: '', notes: '', added_at: new Date().toISOString() },
      ],
    }));
  };
  const patchExtra = (id, patch) => setWorkExecution(prev => ({
    ...prev,
    extras: (prev.extras || []).map(x => x.id === id ? { ...x, ...patch } : x),
  }));
  const removeExtra = (id) => setWorkExecution(prev => ({
    ...prev,
    extras: (prev.extras || []).filter(x => x.id !== id),
  }));

  // ══════════════════════════════════════════════════════════════════
  // Persist
  // ══════════════════════════════════════════════════════════════════
  const persist = async (extraFields = {}) => {
    setSaving(true);
    setSaveErr('');
    try {
      const payload = { ...workExecution, ...extraFields };
      await api.patch(`/appraisals/${appraisalId}`, {
        work_execution: payload,
        work_execution_signed_off: !!payload.signed_off,
        work_execution_signed_off_at: payload.signed_off_at || null,
      });
      setLastSavedAt(new Date().toISOString());
      setWorkExecution(payload);
      return true;
    } catch (e) {
      setSaveErr(e?.response?.data?.detail || e?.message || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const signOffAndClose = async () => {
    if (!workExecution.contractor_signoff_by?.trim()) {
      setSaveErr('Enter the contractor name/signature before signing off.');
      return;
    }
    if (!allLinesTicked) {
      setSaveErr(`All ${totalLineCount} work-breakdown lines must be ticked before sign-off (${tickedLineCount}/${totalLineCount} done).`);
      return;
    }
    const nowIso = new Date().toISOString();
    const ok = await persist({ signed_off: true, signed_off_at: nowIso });
    if (ok) setTimeout(() => window.close(), 400);
  };

  const extrasTotal = (workExecution.extras || []).reduce((s, x) => s + Number(x.cost || 0), 0);
  const canSignOff = !!workExecution.contractor_signoff_by?.trim() && allLinesTicked && !workExecution.signed_off;

  if (loading) return <div className="p-8 text-slate-500 italic">Loading job execution…</div>;

  const toggleChild = (child_id) => setExpandedChildren(prev => ({ ...prev, [child_id]: !prev[child_id] }));
  const toggleGroup = (child_id, ct) => {
    const k = `${child_id}|${ct}`;
    setExpandedGroups(prev => ({ ...prev, [k]: !prev[k] }));
  };

  return (
    <div className="space-y-6" data-testid="job-execution-panel">
      {/* ── Phase 2B · Foreman "Log Today's Session" ──────────────── */}
      <ForemanLogSessionCard appraisalId={appraisalId} />

      {/* ── Contractor sign-off summary ────────────────────────────── */}
      <div className={`p-4 border rounded-xl ${workExecution.signed_off ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-64">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">Contractor sign-off</div>
            <input type="text" value={workExecution.contractor_signoff_by || ''}
              onChange={(e) => setWorkExecution(prev => ({ ...prev, contractor_signoff_by: e.target.value }))}
              placeholder="Name / signature of the contractor signing off completion"
              data-testid="job-exec-signoff-by"
              disabled={workExecution.signed_off}
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded" />
            {workExecution.signed_off && (
              <div className="text-xs text-emerald-700 mt-2">
                ✓ Signed off {workExecution.signed_off_at ? new Date(workExecution.signed_off_at).toLocaleString() : ''}
              </div>
            )}
            {!workExecution.signed_off && (
              <div className={`text-[11px] mt-2 font-semibold ${allLinesTicked ? 'text-emerald-700' : 'text-amber-700'}`}>
                Breakdown progress: {tickedLineCount} of {totalLineCount} lines ticked
                {!allLinesTicked && ` — ${totalLineCount - tickedLineCount} still to tick before sign-off is allowed`}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <button onClick={() => persist()} disabled={saving || workExecution.signed_off}
              data-testid="job-exec-save-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1.5">
              <Save size={12} /> {saving ? 'Saving…' : 'Save progress'}
            </button>
            <button onClick={signOffAndClose} disabled={saving || !canSignOff}
              data-testid="job-exec-signoff-btn"
              className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5">
              <CheckCircle2 size={14} /> Sign off &amp; return to Runner
            </button>
          </div>
        </div>
      </div>

      {saveErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{saveErr}</span>
        </div>
      )}
      {lastSavedAt && !saveErr && (
        <div className="text-xs text-slate-500">Last saved {new Date(lastSavedAt).toLocaleString()}</div>
      )}

      {/* ── Per-child accordions ───────────────────────────────────── */}
      <div className="space-y-4">
        {children.map(c => {
          const cdoc = c.doc || {};
          const isOpen = !!expandedChildren[c.child_id];
          const cRows = contractorRows[c.child_id] || [];
          const breakdown = componentBreakdowns[c.child_id];
          const dailyLogs = (workExecution.per_child_daily_logs || {})[c.child_id] || [];
          const childLineTotal = breakdown ? (breakdown.groups || []).reduce((s, g) => s + (g.lines || []).length, 0) : 0;
          const childLineDone = breakdown ? (breakdown.groups || []).reduce((s, g) => s + (g.lines || []).filter(l => getTick(c.child_id, l.line_id).done).length, 0) : 0;

          return (
            <div key={c.child_id} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
              <button onClick={() => toggleChild(c.child_id)}
                data-testid={`job-exec-child-toggle-${c.child_id}`}
                className="w-full px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap text-left hover:bg-slate-100">
                {isOpen ? <ChevronDown size={16} className="text-slate-600" /> : <ChevronRight size={16} className="text-slate-600" />}
                <ClipboardList size={16} className="text-slate-600" />
                <span className="text-sm font-semibold text-slate-800">
                  {cdoc.quote_type_ref || c.quote_type_ref}
                  {cdoc.variant_label ? ` · ${cdoc.variant_label}` : ''}
                </span>
                {(cdoc.appraisal_no || c.appraisal_no) && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                    A{cdoc.appraisal_no || c.appraisal_no}
                  </span>
                )}
                <span className="ml-auto flex items-center gap-2 text-[11px] text-slate-500">
                  <span>{cRows.length} contractor{cRows.length === 1 ? '' : 's'}</span>
                  <span>·</span>
                  <span className={childLineDone === childLineTotal && childLineTotal > 0 ? 'text-emerald-700 font-semibold' : ''}>
                    {childLineDone}/{childLineTotal} lines ticked
                  </span>
                  <span>·</span>
                  <span>{dailyLogs.length} day{dailyLogs.length === 1 ? '' : 's'} logged</span>
                </span>
              </button>

              {isOpen && (
                <div className="p-4 space-y-5">
                  {/* Contractor summary rows */}
                  {cRows.length > 0 && (
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2">Contractor summary</div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-600">
                            <tr>
                              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Contractor</th>
                              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Started</th>
                              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Completed</th>
                              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Checked</th>
                              <th className="text-left px-3 py-2 font-semibold uppercase tracking-widest">Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cRows.map(r => {
                              const line = getLine(c.child_id, r.ctype);
                              const done = !!(line.start_date && line.completion_date && line.checked_date);
                              return (
                                <tr key={r.ctype} className={`border-t border-slate-100 ${done ? 'bg-emerald-50/40' : ''}`}
                                  data-testid={`job-exec-row-${c.child_id}-${r.ctype}`}>
                                  <td className="px-3 py-2 font-medium text-slate-800">
                                    <div className="flex items-center gap-1.5">
                                      <Wrench size={12} className="text-slate-400" />
                                      {r.ctype}
                                      {done && <CheckCircle2 size={12} className="text-emerald-600" />}
                                    </div>
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <input type="date" value={line.start_date || ''}
                                      onChange={(e) => setLine(c.child_id, r.ctype, { start_date: e.target.value })}
                                      disabled={workExecution.signed_off}
                                      data-testid={`job-exec-start-${c.child_id}-${r.ctype}`}
                                      className="px-1.5 py-1 border border-slate-300 rounded font-mono disabled:opacity-60" />
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <input type="date" value={line.completion_date || ''}
                                      onChange={(e) => setLine(c.child_id, r.ctype, { completion_date: e.target.value })}
                                      disabled={workExecution.signed_off}
                                      data-testid={`job-exec-complete-${c.child_id}-${r.ctype}`}
                                      className="px-1.5 py-1 border border-slate-300 rounded font-mono disabled:opacity-60" />
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <input type="date" value={line.checked_date || ''}
                                      onChange={(e) => setLine(c.child_id, r.ctype, { checked_date: e.target.value })}
                                      disabled={workExecution.signed_off}
                                      data-testid={`job-exec-check-${c.child_id}-${r.ctype}`}
                                      className="px-1.5 py-1 border border-slate-300 rounded font-mono disabled:opacity-60" />
                                  </td>
                                  <td className="px-3 py-1.5">
                                    <input type="text" value={line.notes || ''}
                                      onChange={(e) => setLine(c.child_id, r.ctype, { notes: e.target.value })}
                                      disabled={workExecution.signed_off}
                                      placeholder="Observations…"
                                      data-testid={`job-exec-notes-${c.child_id}-${r.ctype}`}
                                      className="w-full px-1.5 py-1 border border-slate-300 rounded disabled:opacity-60" />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Per-child Sign-off (iter303ak-fix35) — completion date
                      feeds the Certificate of Completion at Paid T20. Auto-
                      suggested from MAX contractor completion_date but
                      operator can override. */}
                  {(() => {
                    const so = getChildSignoff(c.child_id);
                    const suggested = suggestedChildCompletion(c.child_id);
                    const effective = so.completion_date || suggested || '';
                    return (
                      <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3"
                        data-testid={`job-exec-child-signoff-${c.child_id}`}>
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-emerald-800 mb-2 flex items-center gap-2">
                          <CheckCircle2 size={11} /> Child completion sign-off
                          <span className="text-emerald-700/70 font-normal normal-case">— feeds the Certificate of Completion at Paid T20.</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                          <div className="md:col-span-3">
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Completion date</label>
                            <input type="date" value={effective}
                              onChange={(e) => setChildSignoff(c.child_id, { completion_date: e.target.value })}
                              disabled={workExecution.signed_off}
                              data-testid={`job-exec-child-completion-${c.child_id}`}
                              className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono disabled:opacity-60" />
                            {suggested && (
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Suggested (max contractor date): <b className="font-mono">{suggested}</b>
                                {so.completion_date && so.completion_date !== suggested && (
                                  <button onClick={() => setChildSignoff(c.child_id, { completion_date: suggested })}
                                    disabled={workExecution.signed_off}
                                    data-testid={`job-exec-child-reset-suggested-${c.child_id}`}
                                    className="ml-2 text-indigo-700 underline hover:text-indigo-900 disabled:opacity-40">
                                    Reset to suggested
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-9">
                            <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Sign-off notes (optional)</label>
                            <input type="text" value={so.notes || ''}
                              onChange={(e) => setChildSignoff(c.child_id, { notes: e.target.value })}
                              disabled={workExecution.signed_off}
                              placeholder="Anything specific to this child on the day work finished…"
                              data-testid={`job-exec-child-signoff-notes-${c.child_id}`}
                              className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60" />
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Work Breakdown (line-level ticks) */}
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                      <ClipboardList size={11} /> Work breakdown
                      {breakdown && <span className="text-slate-400 font-normal normal-case">— {childLineDone}/{childLineTotal} ticked</span>}
                    </div>
                    {!breakdown && <div className="text-xs text-slate-500 italic p-2">Loading breakdown…</div>}
                    {breakdown && (breakdown.groups || []).length === 0 && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                        No component lines on this child yet. Set them up in the child Components tab.
                      </div>
                    )}
                    {breakdown && (breakdown.groups || []).map(g => {
                      const gKey = `${c.child_id}|${g.component_type}`;
                      const gOpen = !!expandedGroups[gKey];
                      const gDone = (g.lines || []).filter(l => getTick(c.child_id, l.line_id).done).length;
                      const gTot = (g.lines || []).length;
                      return (
                        <div key={g.component_type} className="mb-2 border border-slate-200 rounded overflow-hidden">
                          <button onClick={() => toggleGroup(c.child_id, g.component_type)}
                            data-testid={`job-exec-group-toggle-${c.child_id}-${g.component_type}`}
                            className="w-full px-3 py-1.5 bg-slate-50 flex items-center gap-2 text-left hover:bg-slate-100">
                            {gOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            <span className="text-xs font-semibold text-slate-700">{g.component_type}</span>
                            <span className={`text-[10px] font-semibold ml-auto ${gDone === gTot ? 'text-emerald-700' : 'text-slate-500'}`}>
                              {gDone}/{gTot}
                            </span>
                          </button>
                          {gOpen && (
                            <div className="divide-y divide-slate-100">
                              {(g.lines || []).map(l => {
                                const tick = getTick(c.child_id, l.line_id);
                                return (
                                  <div key={l.line_id} className={`px-3 py-2 ${tick.done ? 'bg-emerald-50/40' : ''}`}
                                    data-testid={`job-exec-line-${c.child_id}-${l.line_id}`}>
                                    <div className="flex items-start gap-3">
                                      <button onClick={() => toggleTick(c.child_id, l.line_id)}
                                        disabled={workExecution.signed_off}
                                        data-testid={`job-exec-line-tick-${c.child_id}-${l.line_id}`}
                                        className="mt-1 shrink-0 disabled:opacity-60">
                                        {tick.done
                                          ? <CheckCircle2 size={16} className="text-emerald-600" />
                                          : <div className="w-4 h-4 border-2 border-slate-300 rounded" />}
                                      </button>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-xs font-medium text-slate-800 truncate">
                                          {l.type_name || 'Line item'}
                                        </div>
                                        <div className="text-[10px] text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
                                          {l.quantity && <span>qty <b className="font-mono">{l.quantity}</b></span>}
                                          {l.length && Number(l.length) > 0 && <span>len <b className="font-mono">{l.length}</b>m</span>}
                                          {l.supplier && <span>· {l.supplier}</span>}
                                          {l.labour_details && <span>· {l.labour_details}</span>}
                                          {tick.done && tick.done_at && <span className="text-emerald-700 font-semibold ml-auto">✓ {new Date(tick.done_at).toLocaleString()}</span>}
                                        </div>
                                        <input type="text" value={tick.notes || ''}
                                          onChange={(e) => setTickNote(c.child_id, l.line_id, e.target.value)}
                                          disabled={workExecution.signed_off}
                                          placeholder="Line notes (optional)…"
                                          data-testid={`job-exec-line-notes-${c.child_id}-${l.line_id}`}
                                          className="w-full mt-1 px-2 py-0.5 text-[11px] border border-slate-300 rounded disabled:opacity-60" />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Daily Log */}
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
                      <CalendarDays size={11} /> Daily log
                      <span className="text-slate-400 font-normal normal-case">— {dailyLogs.length} day{dailyLogs.length === 1 ? '' : 's'} recorded</span>
                    </div>
                    <div className="space-y-2">
                      {dailyLogs.map(dl => {
                        const uk = `${c.child_id}|${dl.id}`;
                        const busyUpload = uploadingKey === uk;
                        return (
                          <div key={dl.id} className="border border-slate-200 rounded bg-slate-50/40 p-3"
                            data-testid={`job-exec-daily-${c.child_id}-${dl.id}`}>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-2">
                              <div className="md:col-span-3">
                                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Date</label>
                                <input type="date" value={dl.date || ''}
                                  onChange={(e) => patchDailyLog(c.child_id, dl.id, { date: e.target.value })}
                                  disabled={workExecution.signed_off}
                                  data-testid={`job-exec-daily-date-${c.child_id}-${dl.id}`}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono disabled:opacity-60" />
                              </div>
                              <div className="md:col-span-2">
                                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                                  <Clock size={10} className="inline mr-1 -mt-0.5" /> Hours
                                </label>
                                <input type="number" step="0.5" min="0" value={dl.hours || ''}
                                  onChange={(e) => patchDailyLog(c.child_id, dl.id, { hours: e.target.value })}
                                  disabled={workExecution.signed_off}
                                  data-testid={`job-exec-daily-hours-${c.child_id}-${dl.id}`}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-mono disabled:opacity-60" />
                              </div>
                              <div className="md:col-span-3">
                                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">
                                  <Cloud size={10} className="inline mr-1 -mt-0.5" /> Weather
                                </label>
                                <input type="text" value={dl.weather || ''}
                                  onChange={(e) => patchDailyLog(c.child_id, dl.id, { weather: e.target.value })}
                                  disabled={workExecution.signed_off}
                                  placeholder="Fine / Wet / Windy …"
                                  data-testid={`job-exec-daily-weather-${c.child_id}-${dl.id}`}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60" />
                              </div>
                              <div className="md:col-span-4">
                                <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-0.5">Work notes</label>
                                <input type="text" value={dl.notes || ''}
                                  onChange={(e) => patchDailyLog(c.child_id, dl.id, { notes: e.target.value })}
                                  disabled={workExecution.signed_off}
                                  placeholder="Iron on north face done · barge tomorrow"
                                  data-testid={`job-exec-daily-notes-${c.child_id}-${dl.id}`}
                                  className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60" />
                              </div>
                            </div>

                            {/* Photos */}
                            <div className="flex items-start gap-2 flex-wrap">
                              {(dl.photos || []).map(p => (
                                <div key={p.attachment_id} className="relative w-16 h-16 border border-slate-300 rounded overflow-hidden bg-white group">
                                  <img src={attachmentSrc(p.attachment_id)} alt={p.filename}
                                    data-testid={`job-exec-daily-photo-${dl.id}-${p.attachment_id}`}
                                    className="w-full h-full object-cover" />
                                  {!workExecution.signed_off && (
                                    <button onClick={() => removePhoto(c.child_id, dl.id, p.attachment_id)}
                                      data-testid={`job-exec-daily-photo-remove-${dl.id}-${p.attachment_id}`}
                                      className="absolute top-0.5 right-0.5 bg-rose-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                                      <X size={10} />
                                    </button>
                                  )}
                                </div>
                              ))}
                              {!workExecution.signed_off && (
                                <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:bg-slate-100"
                                  data-testid={`job-exec-daily-photo-upload-${dl.id}`}>
                                  <input type="file" accept="image/*" multiple={false}
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) uploadPhoto(c.child_id, dl.id, f);
                                      e.target.value = '';
                                    }}
                                    disabled={busyUpload}
                                    className="hidden" />
                                  {busyUpload
                                    ? <ImageIcon size={16} className="text-slate-400 animate-pulse" />
                                    : <Camera size={16} className="text-slate-500" />}
                                </label>
                              )}
                              <button onClick={() => removeDailyLog(c.child_id, dl.id)}
                                disabled={workExecution.signed_off}
                                data-testid={`job-exec-daily-remove-${c.child_id}-${dl.id}`}
                                className="ml-auto self-start text-[11px] text-rose-600 hover:bg-rose-50 rounded px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40">
                                <Trash2 size={11} /> Remove day
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      <button onClick={() => addDailyLog(c.child_id)} disabled={workExecution.signed_off}
                        data-testid={`job-exec-daily-add-${c.child_id}`}
                        className="w-full px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-dashed border-slate-300 hover:bg-slate-50 rounded inline-flex items-center gap-1.5 justify-center disabled:opacity-40">
                        <Plus size={14} /> Add day
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {children.length === 0 && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            No accepted quotes on this appraisal — check the Accepted step in the workflow.
          </div>
        )}
      </div>

      {/* ── Extras Capture ─────────────────────────────────────────── */}
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
          <DollarSign size={16} className="text-slate-600" />
          <span className="text-sm font-semibold text-slate-800">Extras Capture</span>
          <span className="ml-auto text-[11px] text-slate-500">
            {(workExecution.extras || []).length} extra{(workExecution.extras || []).length === 1 ? '' : 's'} · Total <b>${nz(extrasTotal)}</b>
          </span>
        </div>
        <div className="p-3 space-y-2">
          {(workExecution.extras || []).map(x => (
            <div key={x.id} className="p-3 border border-slate-200 rounded-lg bg-slate-50/50" data-testid={`job-exec-extra-${x.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                <input type="text" value={x.description || ''}
                  onChange={(e) => patchExtra(x.id, { description: e.target.value })}
                  disabled={workExecution.signed_off}
                  placeholder="Extra work description"
                  data-testid={`job-exec-extra-desc-${x.id}`}
                  className="px-2 py-1 text-xs border border-slate-300 rounded md:col-span-2 disabled:opacity-60" />
                <input type="number" step="0.01" value={x.cost || ''}
                  onChange={(e) => patchExtra(x.id, { cost: e.target.value })}
                  disabled={workExecution.signed_off}
                  placeholder="Cost $"
                  data-testid={`job-exec-extra-cost-${x.id}`}
                  className="px-2 py-1 text-xs border border-slate-300 rounded font-mono disabled:opacity-60" />
                <button onClick={() => removeExtra(x.id)}
                  disabled={workExecution.signed_off}
                  data-testid={`job-exec-extra-remove-${x.id}`}
                  className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 rounded inline-flex items-center gap-1 justify-center disabled:opacity-40">
                  <Trash2 size={12} /> Remove
                </button>
              </div>
              <input type="text" value={x.notes || ''}
                onChange={(e) => patchExtra(x.id, { notes: e.target.value })}
                disabled={workExecution.signed_off}
                placeholder="Reason / notes (optional)"
                data-testid={`job-exec-extra-notes-${x.id}`}
                className="w-full px-2 py-1 text-xs border border-slate-300 rounded disabled:opacity-60" />
            </div>
          ))}
          <button onClick={addExtra} disabled={workExecution.signed_off}
            data-testid="job-exec-add-extra"
            className="w-full px-3 py-2 text-xs font-semibold text-slate-700 bg-white border-2 border-dashed border-slate-300 hover:bg-slate-50 rounded inline-flex items-center gap-1.5 justify-center disabled:opacity-40">
            <Plus size={14} /> Add extra work item
          </button>
        </div>
      </div>
    </div>
  );
}
